import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Building2, MapPin, Truck, Save, CreditCard, Percent, Plus, Trash2, Store, User } from "lucide-react";
import api from "../lib/api";

const REGIONS = [
  "Lombardia", "Lazio", "Campania", "Sicilia", "Veneto", "Emilia-Romagna", 
  "Piemonte", "Puglia", "Toscana", "Calabria", "Sardegna", "Liguria", 
  "Marche", "Abruzzo", "Friuli-Venezia Giulia", "Trentino-Alto Adige", 
  "Umbria", "Basilicata", "Molise", "Valle d'Aosta"
];

const emptyAddress = {
  label: "",
  address: "",
  city: "",
  postal_code: "",
  contact_name: ""
};

export default function ClientProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState(null);
  const [agent, setAgent] = useState(null); // Correctly placed here
  const [channelDiscount, setChannelDiscount] = useState(0);
  
  // Dialog state for new address
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [newAddress, setNewAddress] = useState(emptyAddress);

  const [formData, setFormData] = useState({
    // Editable fields
    company_name: "",
    vat_number: "",
    fiscal_code: "",
    sdi_code: "",
    pec: "",
    contact_name: "",
    contact_email: "",
    email: "",
    phone: "",
    // Riferimenti Amministrativi
    admin_contact_name: "",
    admin_contact_email: "",
    admin_contact_phone: "",
    // Sede
    address: "",
    city: "",
    region: "",
    postal_code: "",
    delivery_contact: "",
    delivery_address: "",
    delivery_city: "",
    delivery_postal_code: "",
    delivery_notes: "",
    notes: "",
    shipping_addresses: [] // Array of additional addresses
  });

  useEffect(() => {
    if (user?.client_id) {
      fetchClientData();
    }
  }, [user]);

  const fetchClientData = async () => {
    try {
      const response = await api.get(`/clients/${user.client_id}`);
      const data = response.data;
      setClient(data);
      setFormData({
        company_name: data.company_name || "",
        vat_number: data.vat_number || "",
        fiscal_code: data.fiscal_code || "",
        sdi_code: data.sdi_code || "",
        pec: data.pec || "",
        contact_name: data.contact_name || "",
        contact_email: data.contact_email || "",
        email: data.email || "",
        phone: data.phone || "",
        // Riferimenti Amministrativi
        admin_contact_name: data.admin_contact_name || "",
        admin_contact_email: data.admin_contact_email || "",
        admin_contact_phone: data.admin_contact_phone || "",
        // Sede
        address: data.address || "",
        city: data.city || "",
        region: data.region || "",
        postal_code: data.postal_code || "",
        delivery_contact: data.delivery_contact || "",
        delivery_address: data.delivery_address || "",
        delivery_city: data.delivery_city || "",
        delivery_postal_code: data.delivery_postal_code || "",
        delivery_notes: data.delivery_notes || "",
        notes: data.notes || "",
        shipping_addresses: data.shipping_addresses || []
      });

      // Recupera gli sconti canale dalle impostazioni
      try {
        const settingsResponse = await api.get('/settings/channel-discounts');
        const channelDiscounts = {};
        settingsResponse.data.discounts.forEach(d => {
          channelDiscounts[d.channel] = d.discount;
        });
        const discount = channelDiscounts[data.channel] || 0;
        setChannelDiscount(discount);
      } catch (error) {
        console.error("Error fetching channel discounts:", error);
        setChannelDiscount(0);
      }

      // Fetch agent data if assigned
      if (data.agent_id) {
        try {
          const agentRes = await api.get(`/users/${data.agent_id}`);
          setAgent(agentRes.data);
        } catch (e) {
          console.error("Agent info fetch failed", e);
        }
      }
    } catch (error) {
      toast.error("Errore nel caricamento del profilo");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddAddress = () => {
    if (!newAddress.address || !newAddress.city) {
      toast.error("Indirizzo e Città sono obbligatori");
      return;
    }

    const addressToAdd = {
      ...newAddress,
      id: crypto.randomUUID() // Temporary ID for frontend
    };

    setFormData(prev => ({
      ...prev,
      shipping_addresses: [...prev.shipping_addresses, addressToAdd]
    }));

    setNewAddress(emptyAddress);
    setAddressDialogOpen(false);
    toast.success("Sede aggiunta (ricorda di salvare)");
  };

  const handleRemoveAddress = (id) => {
    setFormData(prev => ({
      ...prev,
      shipping_addresses: prev.shipping_addresses.filter(addr => addr.id !== id)
    }));
    toast.info("Sede rimossa (ricorda di salvare)");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...client, 
        ...formData, 
      };

      await api.put(`/clients/${user.client_id}`, payload);
      toast.success("Profilo aggiornato con successo");
      fetchClientData(); 
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-primary">Profilo Azienda</h1>
          <p className="text-muted-foreground">Gestisci i tuoi dati anagrafici e di spedizione</p>
        </div>
        <Button onClick={handleSubmit} disabled={saving} className="bg-accent hover:bg-accent/90">
          {saving ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Salvataggio...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Salva Modifiche
            </div>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Editable Forms */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Anagrafica */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-accent" />
                Dati Anagrafici
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>Ragione Sociale</Label>
                <Input 
                  value={formData.company_name} 
                  onChange={(e) => handleChange("company_name", e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Partita IVA</Label>
                <Input 
                  value={formData.vat_number} 
                  onChange={(e) => handleChange("vat_number", e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Codice Fiscale</Label>
                <Input 
                  value={formData.fiscal_code} 
                  onChange={(e) => handleChange("fiscal_code", e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Codice SDI</Label>
                <Input 
                  value={formData.sdi_code} 
                  onChange={(e) => handleChange("sdi_code", e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>PEC</Label>
                <Input 
                  value={formData.pec} 
                  onChange={(e) => handleChange("pec", e.target.value)} 
                />
              </div>
            </CardContent>
          </Card>

          {/* Sede Legale */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-accent" />
                Sede Legale
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>Indirizzo Sede Legale</Label>
                <Input 
                  value={formData.address} 
                  onChange={(e) => handleChange("address", e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Città</Label>
                <Input 
                  value={formData.city} 
                  onChange={(e) => handleChange("city", e.target.value)} 
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>CAP</Label>
                  <Input 
                    value={formData.postal_code} 
                    onChange={(e) => handleChange("postal_code", e.target.value)} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Provincia</Label>
                  <Input 
                    value={formData.region} 
                    onChange={(e) => handleChange("region", e.target.value)} 
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contatto Principale */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-accent" />
                Contatto Principale
              </CardTitle>
              <CardDescription>Referente per le comunicazioni commerciali</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>Nome e Cognome Referente</Label>
                <Input 
                  value={formData.contact_name} 
                  onChange={(e) => handleChange("contact_name", e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Email Referente</Label>
                <Input 
                  type="email"
                  value={formData.contact_email} 
                  onChange={(e) => handleChange("contact_email", e.target.value)} 
                  placeholder="email@azienda.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input 
                  value={formData.phone} 
                  onChange={(e) => handleChange("phone", e.target.value)} 
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Email Account (Login)</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => handleChange("email", e.target.value)}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">L'email di login non può essere modificata. Contatta l'assistenza per cambiarla.</p>
              </div>
            </CardContent>
          </Card>

          {/* Riferimenti Amministrativi */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-accent" />
                Riferimenti Amministrativi
              </CardTitle>
              <CardDescription>Contatto per fatturazione e comunicazioni amministrative</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>Nome e Cognome</Label>
                <Input 
                  value={formData.admin_contact_name} 
                  onChange={(e) => handleChange("admin_contact_name", e.target.value)} 
                  placeholder="Referente amministrativo"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Amministrazione</Label>
                <Input 
                  type="email"
                  value={formData.admin_contact_email} 
                  onChange={(e) => handleChange("admin_contact_email", e.target.value)} 
                  placeholder="amministrazione@azienda.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefono Amministrazione</Label>
                <Input 
                  value={formData.admin_contact_phone} 
                  onChange={(e) => handleChange("admin_contact_phone", e.target.value)} 
                  placeholder="Telefono ufficio"
                />
              </div>
            </CardContent>
          </Card>

          {/* Spedizione Default */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-accent" />
                Sede Operativa (Principale)
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>Indirizzo Consegna</Label>
                <Input 
                  value={formData.delivery_address} 
                  onChange={(e) => handleChange("delivery_address", e.target.value)} 
                  placeholder="Se diverso dalla sede legale"
                />
              </div>
              <div className="space-y-2">
                <Label>Città Consegna</Label>
                <Input 
                  value={formData.delivery_city} 
                  onChange={(e) => handleChange("delivery_city", e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>CAP Consegna</Label>
                <Input 
                  value={formData.delivery_postal_code} 
                  onChange={(e) => handleChange("delivery_postal_code", e.target.value)} 
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Referente Magazzino</Label>
                <Input 
                  value={formData.delivery_contact} 
                  onChange={(e) => handleChange("delivery_contact", e.target.value)} 
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Note Corriere (Standard)</Label>
                <Textarea 
                  value={formData.delivery_notes} 
                  onChange={(e) => handleChange("delivery_notes", e.target.value)} 
                  placeholder="Orari scarico, sponda idraulica, ecc."
                />
              </div>
            </CardContent>
          </Card>

          {/* Sedi Aggiuntive */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-accent" />
                Sedi Aggiuntive
              </CardTitle>
              <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi Sede
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuova Sede di Spedizione</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Etichetta Sede *</Label>
                      <Input 
                        placeholder="Es. Magazzino Nord" 
                        value={newAddress.label}
                        onChange={(e) => setNewAddress({...newAddress, label: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Indirizzo *</Label>
                      <Input 
                        placeholder="Via Roma 1" 
                        value={newAddress.address}
                        onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>Città *</Label>
                        <Input 
                          placeholder="Milano" 
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CAP *</Label>
                        <Input 
                          placeholder="20100" 
                          value={newAddress.postal_code}
                          onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Referente Loco</Label>
                      <Input 
                        placeholder="Mario Rossi" 
                        value={newAddress.contact_name}
                        onChange={(e) => setNewAddress({...newAddress, contact_name: e.target.value})}
                      />
                    </div>
                    <Button className="w-full bg-accent hover:bg-accent/90" onClick={handleAddAddress}>
                      Aggiungi alla Lista
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {formData.shipping_addresses.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-4">Nessuna sede aggiuntiva registrata</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Etichetta</TableHead>
                      <TableHead>Indirizzo</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.shipping_addresses.map((addr, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{addr.label}</TableCell>
                        <TableCell>
                          <div className="text-sm">{addr.address}</div>
                          <div className="text-xs text-muted-foreground">{addr.city} ({addr.postal_code})</div>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleRemoveAddress(addr.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Read-only Commercial Data */}
        <div className="space-y-6">
          <Card className="bg-slate-50 dark:bg-slate-900 border-l-4 border-l-green-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <Percent className="w-5 h-5" />
                Condizioni Commerciali
              </CardTitle>
              <CardDescription>
                Questi dati sono gestiti dall'amministrazione. Contattaci per modifiche.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">Canale di Vendita</Label>
                <div className="flex items-center justify-between mt-1">
                  <p className="font-medium text-lg">{client?.channel}</p>
                  {channelDiscount > 0 && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      Sconto {channelDiscount}%
                    </span>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-white dark:bg-black/20 rounded-lg border border-slate-200 dark:border-slate-800">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide mb-2 block">I tuoi Sconti Personalizzati</Label>
                <div className="space-y-2">
                  {channelDiscount > 0 && (
                    <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-700">
                      <span className="text-sm text-green-700 dark:text-green-400">Sconto Canale</span>
                      <span className="font-mono font-bold text-green-700 dark:text-green-400">{channelDiscount}%</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span>Sconto 1</span>
                    <span className="font-mono font-bold">{client?.discount_1}%</span>
                  </div>
                  {client?.discount_2 > 0 && (
                    <div className="flex justify-between items-center">
                      <span>Sconto 2</span>
                      <span className="font-mono font-bold">{client?.discount_2}%</span>
                    </div>
                  )}
                  {client?.discount_3 > 0 && (
                    <div className="flex justify-between items-center">
                      <span>Sconto 3</span>
                      <span className="font-mono font-bold">{client?.discount_3}%</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">Metodo di Pagamento</Label>
                <div className="flex items-center gap-2 mt-1">
                  <CreditCard className="w-4 h-4 text-slate-500" />
                  <p className="font-medium">{client?.payment_method || "Non definito"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agent Info Card */}
          {agent && (
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                  <User className="w-5 h-5" />
                  Il tuo Agente
                </CardTitle>
                <CardDescription>
                  Per qualsiasi necessità commerciale, contatta il tuo referente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-muted-foreground text-xs uppercase tracking-wide">Nome</Label>
                  <p className="font-medium text-lg">{agent.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs uppercase tracking-wide">Contatti</Label>
                  <a href={`mailto:${agent.email}`} className="flex items-center gap-2 mt-1 text-blue-600 hover:underline">
                    {agent.email}
                  </a>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
